// v2 musk ox — a front-heavy tundra bull: short legs in pale stockings under a
// shaggy skirt hanging nearly to the ground, a massive body with a high shoulder
// hump and the pale dorsal saddle patch, a broad lowered head under a dark
// forelock, and the signature horn set — a bone boss across the brow with horns
// that drop down the cheeks and hook back up at the tips. Two coats: v0
// near-black brown, v1 grizzled grey-brown. Ported 1:1 from age-of-mcp's
// v2/musk-ox.ts, using the library's shared `wetland` (eyes, quadLegs) helpers.
import * as THREE from "three";
import { defineModel, wetland, type Part } from "../../lib";

const COATS = [
  { skirt: "#28190f", body: "#3a281a", hump: "#44301e", head: "#332417",
    saddle: "#b8a88e", leg: "#2a1d14", stocking: "#cfc4b0", muzzle: "#8a7a64" },
  { skirt: "#332620", body: "#4a382a", hump: "#54402e", head: "#42322a",
    saddle: "#c4b8a4", leg: "#362a20", stocking: "#d8cfc0", muzzle: "#9a8c78" },
];

function muskOxParts(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = wetland.quadLegs(0.06, 0.042, 0.09, 0.011, c.leg,
    { lowerH: 0.032, lowerColor: c.stocking, rFoot: 0.009 });
  parts.push(
    // the shaggy skirt draped almost to the ground
    { geometry: new THREE.IcosahedronGeometry(0.062, 0), color: c.skirt,
      position: [0, 0.07, 0], scale: [1.85, 1.0, 1.15] },
    // massive body and the high shoulder hump
    { geometry: new THREE.IcosahedronGeometry(0.058, 0), color: c.body,
      position: [0.005, 0.105, 0], scale: [1.8, 1.05, 1.0] },
    { geometry: new THREE.IcosahedronGeometry(0.035, 0), color: c.hump,
      position: [0.045, 0.15, 0], scale: [0.9, 0.95, 0.85] },
    // pale saddle patch on the back
    { geometry: new THREE.IcosahedronGeometry(0.03, 0), color: c.saddle,
      position: [-0.025, 0.145, 0], scale: [1.3, 0.5, 0.95] },
    // broad lowered head, grey muzzle, dark forelock fringe
    { geometry: new THREE.IcosahedronGeometry(0.032, 0), color: c.head,
      position: [0.115, 0.095, 0], scale: [1.15, 0.95, 1.05] },
    { geometry: new THREE.IcosahedronGeometry(0.018, 0), color: c.muzzle,
      position: [0.146, 0.082, 0], scale: [1.0, 0.8, 0.95] },
    { geometry: new THREE.IcosahedronGeometry(0.0045, 0), color: "#1a120c",
      position: [0.162, 0.084, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.014, 0), color: c.skirt,
      position: [0.133, 0.117, 0], scale: [0.8, 0.5, 0.9] },
    // bone boss across the brow
    { geometry: new THREE.BoxGeometry(0.026, 0.013, 0.054), color: "#d8cdb8",
      position: [0.112, 0.122, 0] },
    ...wetland.eyes(0.135, 0.103, 0.024, 0.0038),
  );
  // horns: drop down the cheeks, then hook up-forward-out at the tips
  for (const s of [1, -1]) {
    parts.push(
      { geometry: new THREE.ConeGeometry(0.0095, 0.04, 4), color: "#cfc4ae",
        position: [0.112, 0.107, s * 0.04], rotation: [s * 2.5, 0, 0] },
      { geometry: new THREE.ConeGeometry(0.0055, 0.03, 4), color: "#e0d6c2",
        position: [0.118, 0.085, s * 0.052], rotation: [s * 0.5, 0, -0.35] },
    );
  }
  return parts;
}

export const muskOxV2 = defineModel({
  id: "musk-ox-v2",
  scale: 3.29, // real-world size correction (see Model.scale)
  batchId: "fable",
  name: "Musk ox (v2)",
  tags: ["creature","musk","ox","low-poly","age-of-mcp","fable"],
  summary: "Low-poly musk ox.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.16,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => muskOxParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => muskOxParts(1) },
  ],
  animations: [],
});
